import React, { useState, useEffect } from 'react';
import { CompanySettingsTemplate } from '@/templates/BossBaby';
import { useBossBabyCompanySettings } from '@/hooks/useBossBaby';

// Mock companies data - in real implementation, this would come from an API
const mockCompanies = [
  { comId: 16, comNm: "Kims Martial Arts" },
  { comId: 25, comNm: "Seoul Taekwondo Academy" },
  { comId: 33, comNm: "Busan Fighting Club" },
  { comId: 42, comNm: "Incheon Sports Center" },
  { comId: 58, comNm: "Daegu Martial Arts" },
];

export const CompanySettings: React.FC = () => {
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const {
    settings,
    loading,
    fetchCompanySettings,
    updateToolUsage,
    updateTaskUsage,
  } = useBossBabyCompanySettings();

  useEffect(() => {
    if (selectedCompanyId) {
      fetchCompanySettings(selectedCompanyId);
    }
  }, [selectedCompanyId, fetchCompanySettings]);

  const handleCompanySelect = (comId: number) => {
    setSelectedCompanyId(comId);
  };

  const handleToolToggle = async (comId: number, toolId: number, isEnabled: boolean) => {
    try {
      await updateToolUsage(comId, toolId, { isEnabled });
    } catch (error) {
      console.error('Tool toggle error:', error);
    }
  };

  const handleTaskToggle = async (comId: number, taskId: number, isEnabled: boolean) => {
    try {
      await updateTaskUsage(comId, taskId, { isEnabled });
    } catch (error) {
      console.error('Task toggle error:', error);
    }
  };

  const handleResetAll = async () => {
    if (!selectedCompanyId) return;
    
    if (window.confirm('Are you sure you want to reset all settings to global defaults? This action cannot be undone.')) {
      try {
        // Reset all tools to global settings
        const resetToolPromises = settings.tools
          .filter(tool => tool.companySetting)
          .map(tool => updateToolUsage(selectedCompanyId, tool.toolId, { isEnabled: tool.isActive }));
        
        // Reset all tasks to global settings
        const resetTaskPromises = settings.tasks
          .filter(task => task.companySetting)
          .map(task => updateTaskUsage(selectedCompanyId, task.taskId, { isEnabled: task.isActive }));
        
        await Promise.all([...resetToolPromises, ...resetTaskPromises]);
        
        // Refresh the settings
        await fetchCompanySettings(selectedCompanyId);
      } catch (error) {
        console.error('Reset all error:', error);
      }
    }
  };

  return (
    <CompanySettingsTemplate
      companies={mockCompanies}
      selectedCompanyId={selectedCompanyId}
      settings={settings}
      loading={loading}
      onCompanySelect={handleCompanySelect}
      onToolToggle={handleToolToggle}
      onTaskToggle={handleTaskToggle}
      onResetAll={handleResetAll}
    />
  );
};